import { FaRocket, FaLightbulb, FaLinkedin, FaUsers, FaGlobe, FaHandshake } from 'react-icons/fa';
import styles from './About.module.scss';
import { SetStateAction, useState, useEffect } from 'react';
import {
  HERO,
  MISSION,
  VISION,
  CORE_VALUES,
  LEADERSHIP,
  CULTURE_ITEMS,
  STORY,
  COMMUNITY_SECTION,
  COMMUNITY_PARTNERS,
} from '../../content/about';
import Seo, { seoPropsFor } from '../../components/Seo/Seo';
import PageHero from '../../components/PageHero/PageHero';
import { PAGE_SEO } from '../../content/site';
import heroImage from '../../assets/banner30-hero.webp';

const VALUE_ICONS = {
  handshake: FaHandshake,
  users: FaUsers,
  lightbulb: FaLightbulb,
  globe: FaGlobe,
} as const;

const CULTURE_ICONS = {
  users: FaUsers,
  lightbulb: FaLightbulb,
  globe: FaGlobe,
} as const;

const COMMUNITY_LOGO_CLASS = {
  familyPromise: 'logoFamilyPromise',
  goodwill: 'logoGoodwill',
  riseUp: 'logoRiseUpwithServiceNow',
  raspberryJam: 'logoRaspberryJam',
} as const;

const AboutPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger the animation when component mounts
    setIsVisible(true);
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === LEADERSHIP.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? LEADERSHIP.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index: SetStateAction<number>) => {
    setCurrentIndex(index);
  };

  return (
    <div className={styles.aboutPage}>
      <Seo {...seoPropsFor(PAGE_SEO.about)} />

      <div className={isVisible ? 'main-content' : ''}>
        <PageHero
          title={HERO.title}
          subtitle={HERO.subtitle}
          image={heroImage}
          tone="about"
        />

        {/* Mission/Vision Cards Section */}
        <section className={styles.missionVision} id="mission">
          <div className={styles.container}>
            <div className={styles.mvGrid}>
              {/* Mission Card */}
              <div className={styles.mvCard}>
                <div className={styles.cardContent}>
                  <h2 className={styles.cardTitle}>
                    <FaRocket className={styles.icon} /> Mission
                  </h2>
                  <div className={styles.cardText}>
                    {MISSION.paragraphs.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Vision Card */}
              <div className={styles.mvCard}>
                <div className={styles.cardContent}>
                  <h2 className={styles.cardTitle}>
                    <FaLightbulb className={styles.icon} /> Vision
                  </h2>
                  <div className={styles.cardText}>
                    {VISION.paragraphs.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Details Section */}
        <section className={styles.valuesDetail} id="values">
          <div className={styles.container}>
            <h2 style={{textAlign: 'center', marginBottom: '40px', color: 'white'}}>Our Core Values</h2>
            <div className={styles.valuesGrid} style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '30px'}}>
              {CORE_VALUES.map((value) => {
                const Icon = VALUE_ICONS[value.iconKey];
                return (
                  <div key={value.id} className={styles.valueItem} style={{textAlign: 'center', padding: '20px', borderRadius: '8px', backgroundColor: 'rgba(255, 255, 255, 0.1)'}}>
                    <div className={styles.valueIcon} style={{fontSize: '2.5rem', marginBottom: '15px', color: 'white'}}>
                      <Icon />
                    </div>
                    <h3 style={{color: 'white', marginBottom: '15px'}}>{value.title}</h3>
                    <ul style={{textAlign: 'left', color: 'rgba(255, 255, 255, 0.9)', paddingLeft: '20px'}}>
                      {value.bullets.map((bullet, i) => (
                        <li key={i}>{bullet}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Leadership Section */}
        <section className={styles.leadership} id="leadership">
          <div className={styles.container}>
            <h2>Our Team</h2>
            <div className={styles.carousel}>
              <div
                className={styles.carouselTrack}
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {LEADERSHIP.map((leader, index) => (
                  <div key={index} className={styles.leaderCard}>
                    <div className={styles.leaderImageContainer}>
                      <img
                        src={leader.image}
                        alt={leader.name}
                        className={styles.leaderImage}
                        onError={(e) => {
                          // Fallback if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallbackDiv = document.createElement('div');
                          fallbackDiv.className = styles.initialsFallback;
                          fallbackDiv.textContent = leader.name.split(' ').map(n => n[0]).join('');
                          target.parentNode?.insertBefore(fallbackDiv, target);
                        }}
                      />
                    </div>
                    <div className={styles.leaderInfo}>
                      <h3>{leader.name}</h3>
                      <p className={styles.title}>{leader.role}</p>
                      <p className={styles.bio}>{leader.bio}</p>
                      <div className={styles.expertise}>
                        {leader.expertise.map((skill, i) => (
                          <span key={i} className={styles.skillTag}>{skill}</span>
                        ))}
                      </div>
                      <a
                        href={leader.linkedin}
                        className={styles.linkedinButton}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Connect with ${leader.name} on LinkedIn`}
                      >
                        <FaLinkedin /> Connect
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              <button className={`${styles.carouselButton} ${styles.prev}`} onClick={prevSlide}>&#10094;</button>
              <button className={`${styles.carouselButton} ${styles.next}`} onClick={nextSlide}>&#10095;</button>
              <div className={styles.carouselDots}>
                {LEADERSHIP.map((_, index) => (
                  <span
                    key={index}
                    className={`${styles.dot} ${currentIndex === index ? styles.active : ''}`}
                    onClick={() => goToSlide(index)}
                  ></span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Team Culture Section */}
        <section className={styles.teamCulture} id="culture">
          <div className={styles.container}>
            <h2>Our Team Culture</h2>
            <div className={styles.cultureGrid}>
              {CULTURE_ITEMS.map((item, index) => {
                const Icon = CULTURE_ICONS[item.iconKey];
                return (
                  <div key={index} className={styles.cultureItem}>
                    <Icon className={styles.icon} />
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Story Section (Replaces Timeline) */}
        <section className={styles.storySection} id="journey">
          <div className={styles.container}>
            <h2>Our Journey</h2>
            <div className={styles.storyContent}>
              <div className={styles.storyMain}>
                <h3>{STORY.heading}</h3>
                {STORY.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}

                <div className={styles.storyHighlights}>
                  <div className={styles.highlightItem}>
                    <div className={styles.highlightIcon}>
                      <FaUsers />
                    </div>
                    <div className={styles.highlightContent}>
                      <h4>{STORY.internshipProgram.title}</h4>
                      <p>{STORY.internshipProgram.body}</p>
                    </div>
                  </div>

                  <div className={styles.highlightItem}>
                    <div className={styles.highlightIcon}>
                      <FaHandshake />
                    </div>
                    <div className={styles.highlightContent}>
                      <h4>{STORY.meetupSponsor.title}</h4>
                      <p>
                        {STORY.meetupSponsor.body.split('info@bizsolutions.tech').map((part, i, arr) => (
                          <span key={i}>
                            {part}
                            {i < arr.length - 1 && (
                              <a href="mailto:info@bizsolutions.tech">info@bizsolutions.tech</a>
                            )}
                          </span>
                        ))}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hide Success Stories for now until further approval */}
        {/* Success Stories Section */}
        {/* <section className={styles.successStories}>
          <div className={styles.container}>
            <h2>Success Stories</h2>
            <div className={styles.storiesGrid}>
              <div className={styles.storyCard}>
                <h3>Global Retail Chain</h3>
                <p>Implemented ServiceNow across 200+ locations, reducing IT ticket resolution time by 65%.</p>
              </div>
              <div className={styles.storyCard}>
                <h3>Healthcare Provider</h3>
                <p>Custom AI solution improved patient scheduling efficiency by 40%.</p>
              </div>
            </div>
          </div>
        </section> */}

        {/* Community Support Section -- charities the company donates to,
            not client references. See Task 5 in REMEDIATION_NOTES.md. */}
        <section className={styles.communitySupport} id="communitySupport">
          <div className={styles.container}>
            <p className={styles.communityKicker}>{COMMUNITY_SECTION.kicker}</p>
            <h2>{COMMUNITY_SECTION.heading}</h2>
            <p>{COMMUNITY_SECTION.intro}</p>
            <div className={styles.commGrid}>
              {COMMUNITY_PARTNERS.map((partner) => (
                <div className={styles.commItem} key={partner.name}>
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className={styles[COMMUNITY_LOGO_CLASS[partner.logoClassKey]]}
                  />
                  <h3>
                    <a href={partner.url} target="_blank" rel="noopener noreferrer" className={styles.commLink}>
                      {partner.name}
                    </a>
                  </h3>
                  <span className={styles.commBadge}>{COMMUNITY_SECTION.badgeLabel}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
